/**
 * React Query hooks for file entries.
 * React Query hooks for the corresponding entity.
 */

import {
  QueryClient,
  useMutation,
  useQuery,
  useQueryClient,
} from "@tanstack/react-query";
import { queryKeys } from "common/queryKeys";
import { API } from "common/utils";
import { FileEntry } from "./FileEntry";

// Re-export types for convenience
export {
  FILE_ENTRY_REJECTION_PERIOD_DAYS,
  FILE_UPLOAD_TYPE,
} from "./fileEntryTypes";
export type { FileEntry };

export type CreateFileEntryPayload = {
  fileDescription: string;
  fileUploadId: string;
  associatedUserId: string;
  fileUploadType: string;
  userId: string;
};

/**
 * Extended API class for file entries
 */
class FileEntryAPI extends API<FileEntry> {
  async getAllFileEntriesForUser(
    associatedUserId?: string
  ): Promise<FileEntry[]> {
    const query = associatedUserId
      ? `?associatedUserId=${associatedUserId}`
      : "";
    const res = await fetch(`${this.baseUrl}${this.entity}${query}`, {
      method: "GET",
      headers: {
        Authorization: `Bearer ${window.localStorage.getItem("jwt") || ""}`,
        "Content-Type": "application/json",
      },
    });
    return (await res.json()) as FileEntry[];
  }

  async createFileEntry(
    payload: Omit<CreateFileEntryPayload, "userId">
  ): Promise<{ guid: string }> {
    const res = await fetch(`${this.baseUrl}${this.entity}`, {
      method: "POST",
      headers: {
        Authorization: `Bearer ${window.localStorage.getItem("jwt") || ""}`,
        "Content-Type": "application/json",
      },
      body: JSON.stringify({
        fileDescription: payload.fileDescription,
        fileUploadId: payload.fileUploadId,
        associatedUserId: payload.associatedUserId,
        fileUploadType: payload.fileUploadType,
      }),
    });

    if (!res.ok) {
      throw new Error(`HTTP error! Status: ${res.status}`);
    }

    return (await res.json()) as { guid: string };
  }
}

const fileEntryApi = new FileEntryAPI("fileEntry");

/**
 * Fetch all file entries for a user
 */
export function useFileEntriesForUser(associatedUserId: string | undefined) {
  return useQuery({
    queryKey: queryKeys.fileEntries.byUser(associatedUserId ?? ""),
    queryFn: async () => {
      if (!associatedUserId) {
        return [];
      }
      const entries = await fileEntryApi.getAllFileEntriesForUser();
      // Sort by creation date descending
      return entries.sort((a, b) => {
        return (
          Date.parse(b.creationDate ?? "") - Date.parse(a.creationDate ?? "")
        );
      });
    },
    enabled: !!associatedUserId,
  });
}

/**
 * Fetch a single file entry by ID
 */
export function useFileEntry(fileEntryId: string | undefined) {
  return useQuery({
    queryKey: queryKeys.fileEntries.detail(fileEntryId ?? ""),
    queryFn: async () => {
      if (!fileEntryId) {
        throw new Error("File entry ID is required");
      }
      return await fileEntryApi.getOne(fileEntryId);
    },
    enabled: !!fileEntryId,
  });
}

/**
 * Create a new file entry
 */
export function useCreateFileEntry() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (payload: CreateFileEntryPayload) => {
      const { userId, ...apiPayload } = payload;
      const response = await fileEntryApi.createFileEntry(apiPayload);
      // Fetch the created entry to get the full object
      const created = await fileEntryApi.getOne(response.guid);
      return created;
    },
    onSuccess: async (data, variables) => {
      // Invalidate the file entries list for this user
      await queryClient.invalidateQueries({
        queryKey: queryKeys.fileEntries.byUser(variables.associatedUserId),
      });
    },
  });
}

/**
 * Update a file entry
 */
export function useUpdateFileEntry() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (fileEntry: FileEntry) => {
      const { userId, ...payload } = fileEntry;
      await fileEntryApi.updateOne(payload);
      // Fetch the updated entry
      return await fileEntryApi.getOne(fileEntry.guid);
    },
    onSuccess: async (data) => {
      // Invalidate relevant queries
      await queryClient.invalidateQueries({
        queryKey: queryKeys.fileEntries.byUser(data.associatedUserId),
      });
      // Update the cache directly
      queryClient.setQueryData(queryKeys.fileEntries.detail(data.guid), data);
    },
  });
}

/**
 * Delete a file entry
 */
export function useDeleteFileEntry() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (fileEntry: FileEntry) => {
      const res = await fileEntryApi.deleteOne(fileEntry.guid);
      if (!res.ok) {
        throw new Error(`Failed to delete file entry: ${res.status}`);
      }
      return fileEntry;
    },
    onSuccess: async (deletedEntry) => {
      // Invalidate the file entries list for this user
      await queryClient.invalidateQueries({
        queryKey: queryKeys.fileEntries.byUser(deletedEntry.associatedUserId),
      });
    },
  });
}

/**
 * Prefetch file entries for a user (used during initialization)
 */
export async function fetchFileEntriesForUserQuery(
  associatedUserId: string,
  queryClient: QueryClient
): Promise<FileEntry[]> {
  const entries = await fileEntryApi.getAllFileEntriesForUser();
  const sorted = entries.sort((a, b) => {
    return Date.parse(b.creationDate ?? "") - Date.parse(a.creationDate ?? "");
  });
  queryClient.setQueryData(
    queryKeys.fileEntries.byUser(associatedUserId),
    sorted
  );
  return sorted;
}

/**
 * Reset file entries query cache
 */
export function resetFileEntriesQueryCache(queryClient: QueryClient): void {
  queryClient.removeQueries({ queryKey: queryKeys.fileEntries.all });
}
